import { Mail, Phone, MapPin, Navigation, Clock, MessageCircle, HeartHandshake, Home as HomeIcon, Newspaper } from "lucide-react";
import { Link } from "react-router-dom";
import {
  PageHero,
  SectionHeading,
  TrusteesCallout,
  mailto,
} from "@/components/site/PageParts";
import { Reveal } from "@/components/site/Reveal";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT, ASSETS } from "@/data/site";
import { Seo } from "@/components/site/Seo";

const MAPS_QUERY = "Sandyflat, Caldercuilt Road, Summerston, Glasgow, G23 5NA";
const MAPS_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(MAPS_QUERY)}&output=embed`;
const MAPS_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(MAPS_QUERY)}`;

const INTENTS = [
  { label: "General enquiry", subject: "General enquiry", Icon: MessageCircle },
  { label: "Riding enquiry", subject: "Riding enquiry", Icon: HeartHandshake },
  { label: "Volunteer with us", subject: "Volunteering enquiry", Icon: HeartHandshake },
  { label: "Facility hire", subject: "Facility hire enquiry", Icon: HomeIcon },
  { label: "Media / press", subject: "Media / press enquiry", Icon: Newspaper },
];

const FAQS = [
  {
    q: "Do I need any riding experience?",
    a: "No — not at all. Our qualified coaches and trained volunteers support every rider, whatever their starting point.",
  },
  {
    q: "How much does it cost?",
    a: "We aim to keep sessions affordable. Please contact us for current session information.",
  },
  {
    q: "How do I refer someone?",
    a: "Get in touch and we'll walk you through our assessment process and next steps.",
  },
  {
    q: "Can I visit before signing up?",
    a: "Yes — please arrange a visit with our coordinator and come and see us in action.",
  },
  {
    q: "How can I support RDA Glasgow?",
    a: "There are lots of ways to help, from volunteering to fundraising and in-kind gifts.",
    link: { to: "/support-us", label: "See ways to support us" },
  },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <Seo title="Contact — RDA Glasgow" description="Get in touch with RDA Glasgow. Find our address, phone and email, view us on the map, and see answers to frequently asked questions." image={ASSETS.facilityExterior} />
      <PageHero
        title="Get in touch"
        subtitle="We'd love to hear from you — whether you're a rider, a volunteer or a supporter."
        image={ASSETS.facilityExterior}
        imageAlt="The RDA Glasgow centre in Summerston"
      />

      <section className="bg-rda-offwhite">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="grid gap-10 lg:grid-cols-2">
            {/* Left: details */}
            <Reveal>
              <div className="space-y-5">
                <SectionHeading eyebrow="Contact details" title="How to reach us" />

                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5 shadow-soft transition-colors hover:border-rda-teal">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-rda-teal/10 text-rda-teal"><Mail className="h-5 w-5" /></span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wide text-rda-muted">Email</span>
                    <span className="block text-rda-ink font-medium">{CONTACT.email}</span>
                  </span>
                </a>

                <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-phone" className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5 shadow-soft transition-colors hover:border-rda-teal">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-rda-teal/10 text-rda-teal"><Phone className="h-5 w-5" /></span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wide text-rda-muted">Phone</span>
                    <span className="block text-rda-ink font-medium">{CONTACT.phone}</span>
                  </span>
                </a>

                <div className="rounded-2xl border border-border bg-white p-5 shadow-soft" data-testid="contact-address">
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-rda-teal/10 text-rda-teal"><MapPin className="h-5 w-5" /></span>
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-wide text-rda-muted">Address</span>
                      <span className="block text-rda-ink font-medium">{CONTACT.address}</span>
                    </span>
                  </div>
                  <Button asChild variant="outline" className="mt-4 rounded-full border-rda-teal text-rda-teal hover:bg-rda-teal hover:text-white font-semibold" data-testid="contact-directions">
                    <a href={MAPS_DIRECTIONS} target="_blank" rel="noopener noreferrer">
                      <Navigation className="mr-2 h-4 w-4" /> Get directions
                    </a>
                  </Button>
                </div>

                {/* Opening hours — no invented times */}
                <div className="rounded-2xl border border-border bg-white p-5 shadow-soft" data-testid="contact-hours">
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-rda-teal/10 text-rda-teal"><Clock className="h-5 w-5" /></span>
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-wide text-rda-muted">Opening hours</span>
                      {/* TODO: verify opening hours with RDA */}
                      <span className="block text-rda-ink">Sessions run during school terms — please contact us to confirm times.</span>
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Right: map */}
            <Reveal delay={0.1}>
              <div className="h-full min-h-[360px] overflow-hidden rounded-3xl border border-border shadow-soft">
                <iframe
                  title="Map showing RDA Glasgow location at G23 5NA"
                  src={MAPS_EMBED}
                  data-testid="contact-map"
                  className="h-full min-h-[360px] w-full"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Intent buttons */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
          <SectionHeading eyebrow="Quick contact" title="What can we help with?">
            Pick a topic and your email will open with a subject ready to go.
          </SectionHeading>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="contact-intents">
            {INTENTS.map((intent) => (
              <Button
                key={intent.subject}
                asChild
                className="rounded-full bg-rda-teal text-white hover:bg-rda-tealDark font-semibold min-h-[44px]"
                data-testid={`contact-intent-${intent.subject.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                <a href={mailto(intent.subject)}>
                  <intent.Icon className="mr-2 h-4 w-4" /> {intent.label}
                </a>
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-rda-offwhite">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="FAQ" title="Frequently asked questions" />
          <Accordion type="single" collapsible className="mt-8 space-y-3" data-testid="contact-faq">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="rounded-2xl border border-border bg-white px-5 shadow-soft"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left font-heading font-semibold text-rda-ink hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-rda-muted">
                  {faq.a}
                  {faq.link && (
                    <Link to={faq.link.to} className="ml-1 font-semibold text-rda-teal hover:underline" data-testid="faq-support-link">
                      {faq.link.label}
                    </Link>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <TrusteesCallout />
    </div>
  );
}
